import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { StyledAreaDevelopment } from "./AreaDevelopment.styled";
import { ReactComponent as UsersIcon } from "../../icons/users.svg";
import { ReactComponent as TargetIcon } from "../../icons/target.svg";
import { ReactComponent as ContextIcon } from "../../icons/context.svg";
import { ReactComponent as StrenghtsIcon } from "../../icons/strenghts.svg";
import { ReactComponent as DevelopmentScenariosIcon } from "../../icons/developmentScenarios.svg";
import { ReactComponent as SaveIcon } from "../../icons/save.svg";
import { PageNames } from "../../types/AppTypes";
import { Divider } from "../../components/core/Divider/Divider";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../components/core/Button/Button";
import {
  AREA_DEVELOPMENT,
  CURRENT_RESOURCES,
  FORM_COMMENT,
  CONTEXT,
  RESOURCES_NUMBER,
  SAVE_LABEL,
  STRENGTHS,
  IMPROVEMENT_ASSETS,
  DEVELOPMENT_SCENARIOS,
} from "../../constants/Dictionary";
import { FormSection } from "../../components/FormSection/FormSection";
import NumberInput from "../../components/core/NumberInput/NumberInput";
import { useEffect } from "react";
import { RoundedContainer } from "../../components/RoundedContainer/RoundedContainer";
import { Loader } from "../../components/core/Loader/Loader";
import { useBreadCrumb } from "../../hooks/useBreadCrumbs.hooks";
import {
  useAreaDevelopment,
  useUpdateAreaDevelopment,
} from "../../hooks/areaDevelopment.hooks";
import { CHARACTER_MAX_LENGTH } from "../../constants/AppConstants";
import TextArea from "../../components/core/TextArea/TextArea";

type FormData = {
  context?: string;
  strengthsComment?: string;
  assetImprovement?: string;
  developmentScenario?: string;
};

export const AreaDevelopment = () => {
  const { data, isFetching } = useAreaDevelopment();
  const {
    mutate: updateAreaDevelopment,
    isPending: isUpdatingAreaDevelopment,
  } = useUpdateAreaDevelopment();

  const schema = yup
    .object({
      context: yup.string(),
      strengthsComment: yup.string(),
      assetImprovement: yup.string(),
      developmentScenario: yup.string(),
    })
    .required();

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: FormData) => {
    const body = {
      context: data.context ?? null,
      strengthsComment: data.strengthsComment ?? null,
      assetImprovement: data.assetImprovement ?? null,
      developmentScenario: data.developmentScenario ?? null,
    };

    updateAreaDevelopment(body);
  };

  useEffect(() => {
    if (data) {
      reset({
        context: data.context ?? "",
        strengthsComment: data.strengthsComment ?? "",
        assetImprovement: data.assetImprovement ?? "",
        developmentScenario: data.developmentScenario ?? "",
      });
    }
  }, [data]);

  useBreadCrumb([
    {
      name: AREA_DEVELOPMENT,
      pathName: PageNames.AREA_DEVELOPMENT,
    },
  ]);

  return (
    <RoundedContainer fullHeight>
      <StyledAreaDevelopment>
        <Loader loading={isFetching || isUpdatingAreaDevelopment}>
          <div className="content-container">
            <form id="areaDevelopment" onSubmit={handleSubmit(onSubmit)}>
              <FormSection label={CURRENT_RESOURCES} icon={<UsersIcon />}>
                <div className="small-input">
                  <NumberInput
                    value={data?.actualOrgResources}
                    label={RESOURCES_NUMBER}
                    disabled
                  />
                </div>
              </FormSection>
              <Divider />
              <FormSection label={CONTEXT} icon={<ContextIcon />}>
                <Controller
                  name="context"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label={FORM_COMMENT}
                      maxLength={CHARACTER_MAX_LENGTH}
                      {...field}
                    />
                  )}
                />
              </FormSection>
              <Divider />
              <FormSection label={STRENGTHS} icon={<StrenghtsIcon />}>
                <Controller
                  name="strengthsComment"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label={FORM_COMMENT}
                      maxLength={CHARACTER_MAX_LENGTH}
                      {...field}
                    />
                  )}
                />
              </FormSection>
              <Divider />
              <FormSection label={IMPROVEMENT_ASSETS} icon={<TargetIcon />}>
                <Controller
                  name="assetImprovement"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label={FORM_COMMENT}
                      maxLength={CHARACTER_MAX_LENGTH}
                      {...field}
                    />
                  )}
                />
              </FormSection>
              <Divider />
              <FormSection
                label={DEVELOPMENT_SCENARIOS}
                icon={<DevelopmentScenariosIcon />}
              >
                <Controller
                  name="developmentScenario"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label={FORM_COMMENT}
                      maxLength={CHARACTER_MAX_LENGTH}
                      {...field}
                    />
                  )}
                />
              </FormSection>
            </form>
          </div>
        </Loader>
        <div className="footer">
          <div className="footer-items">
            <Button
              disabled={!isDirty || isFetching || isUpdatingAreaDevelopment}
              type="submit"
              form="areaDevelopment"
              label={SAVE_LABEL}
              variant={ButtonVariants.PRIMARY}
              size={ButtonSizes.REGULAR}
              icon={<SaveIcon />}
            />
          </div>
        </div>
      </StyledAreaDevelopment>
    </RoundedContainer>
  );
};
